// ============================================================================
// MÓDULO: AUTH ACTION HANDLER (HANDLER DE E-MAILS DO FIREBASE)
// OBJETIVO: Intercepta links de "Resetar Senha" para exibir UI em Português.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset, applyActionCode } from 'firebase/auth';
import { auth } from '../../../shared/api/firebase/config';
import { ASSETS } from '../../../config/Config';

const AuthActionHandler = ({ mode, oobCode }) => {
    const navigate = useNavigate();
    const [step, setStep] = useState('VERIFYING'); // VERIFYING, INPUT, SUCCESS, ERROR
    const [newPassword, setNewPassword] = useState('');
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        const verifyCode = async () => {
            if (!oobCode) {
                setStep('ERROR');
                setError('Código de verificação inválido ou ausente.');
                return;
            }

            try {
                if (mode === 'resetPassword') {
                    const emailAddr = await verifyPasswordResetCode(auth, oobCode);
                    setEmail(emailAddr);
                    setStep('INPUT');
                } else if (mode === 'verifyEmail') {
                    await applyActionCode(auth, oobCode);
                    setStep('SUCCESS_EMAIL');
                } else {
                    setStep('ERROR');
                    setError('Modo de ação desconhecido.');
                }
            } catch (e) {
                setStep('ERROR');
                setError(e.message || "O link expirou ou já foi utilizado.");
            }
        };
        verifyCode();
    }, [mode, oobCode]);

    const handleResetSubmit = async (e) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            setError("A senha deve ter no mínimo 6 caracteres.");
            return;
        }

        try {
            await confirmPasswordReset(auth, oobCode, newPassword);
            setStep('SUCCESS_RESET');
            
            // REDIRECIONAMENTO AUTOMÁTICO (SOLICITADO)
            setTimeout(() => {
                navigate('/portal/login');
            }, 3000);

        } catch (e) {
            setError(e.message);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 animate-fade-in-up">
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200 w-full max-w-md text-center">
                
                <div className="mb-6 flex justify-center">
                    <img src={ASSETS.logoIcon} alt="RDA" width="48" height="48" className="h-12 w-12 aspect-square object-contain block" loading="eager" decoding="async" />
                </div>

                {/* ETAPA 1: VERIFICANDO LINK */}
                {step === 'VERIFYING' && (
                    <div className="space-y-4">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue mx-auto"></div>
                        <p className="text-gray-500 text-sm">Validando link de segurança...</p>
                    </div>
                )}

                {/* ETAPA 2: DIGITAR NOVA SENHA */}
                {step === 'INPUT' && (
                    <form onSubmit={handleResetSubmit} className="space-y-6 text-left">
                        <h2 className="text-xl font-bold text-brand-blue text-center">Redefinir Senha</h2>
                        <p className="text-xs text-gray-500 text-center">Conta: <strong>{email}</strong></p>
                        
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nova Senha</label>
                            <input 
                                type="password" 
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full p-3 border rounded-lg text-sm focus:border-brand-blue outline-none"
                                placeholder="Digite a nova senha segura"
                                autoFocus
                            />
                        </div>

                        {error && <p className="text-red-500 text-xs font-bold text-center">{error}</p>}

                        <button className="w-full bg-brand-blue text-white py-3 rounded-lg font-bold shadow-lg hover:bg-brand-blue-darker transition-all">
                            SALVAR NOVA SENHA
                        </button>
                    </form>
                )}

                {/* ETAPA 3: SUCESSO (SENHA) */}
                {step === 'SUCCESS_RESET' && (
                    <div className="space-y-4">
                        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto text-2xl">
                            <i className="fas fa-check"></i>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800">Senha Alterada!</h2>
                        <p className="text-sm text-gray-500">Você será redirecionado para o login em instantes.</p>
                        <button onClick={() => navigate('/portal/login')} className="text-brand-blue font-bold text-xs underline">
                            Ir para Login agora
                        </button>
                    </div>
                )}

                {/* ETAPA 4: SUCESSO (EMAIL VERIFICADO) */}
                {step === 'SUCCESS_EMAIL' && (
                    <div className="space-y-4">
                        <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto text-2xl">
                            <i className="fas fa-envelope-check"></i>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800">E-mail Verificado!</h2>
                        <p className="text-sm text-gray-500">Sua conta foi confirmada com sucesso.</p>
                        <button onClick={() => navigate('/portal/login')} className="w-full bg-brand-blue text-white py-3 rounded-lg font-bold shadow-lg">
                            ACESSAR CONTA
                        </button>
                    </div>
                )}

                {/* ETAPA 5: ERRO */}
                {step === 'ERROR' && (
                    <div className="space-y-4">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto text-2xl">
                            <i className="fas fa-times"></i>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800">Link Inválido</h2>
                        <p className="text-sm text-gray-500">{error}</p>
                        <button onClick={() => navigate('/')} className="text-gray-400 font-bold text-xs hover:text-gray-600">
                            Voltar ao Início
                        </button>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AuthActionHandler;